import moment from 'moment';

// A state machine's error path ends SUCCEEDED, so a SUCCEEDED run can still have
// stopped on a caught error: caughtError (vendor-execution-status) carries its
// outcome — FAILED, NOT_CONFIGURED (vendor not set up yet) or STOPPED (deliberate,
// e.g. vendor disabled; shown as succeeded).
export const caughtOutcome = (execStatus, outcome) =>
  execStatus?.executionStatus === 'SUCCEEDED' && execStatus.caughtError?.outcome === outcome
    ? execStatus.caughtError
    : null;

// Map the vendor-execution-status response to the status strings consumed by
// TabOne's getStatusColor / getStatusIcon. `configured: false` comes from the
// vendor's saved configuration (STRY0038866), so an unconfigured vendor never
// shows the result of an old run.
export const mapExecutionStatus = execStatus => {
  if (execStatus?.configured === false) return 'NOT CONFIGURED';
  if (!execStatus?.hasExecution) return 'UNKNOWN';
  if (execStatus.isRunning) return 'RUNNING';
  switch (execStatus.executionStatus) {
    case 'SUCCEEDED':
      if (caughtOutcome(execStatus, 'FAILED')) return 'ERROR';
      if (caughtOutcome(execStatus, 'NOT_CONFIGURED')) return 'NOT CONFIGURED';
      return 'OPERATIONAL';
    case 'FAILED':
    case 'TIMED_OUT':
    case 'ABORTED':
    case 'PENDING_REDRIVE':
      return 'ERROR';
    default:
      return 'UNKNOWN';
  }
};

const TERMINAL_VERBS = {
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  TIMED_OUT: 'timed out',
  ABORTED: 'was aborted',
  PENDING_REDRIVE: 'pending redrive'
};

export const getExecutionText = (execStatus, loading) => {
  if (loading && !execStatus) return 'Waiting for service status...';
  if (execStatus?.configured === false) {
    return execStatus.notReadyReason === 'vendor_inactive'
      ? 'Not active for this tenant'
      : "Not configured — save this vendor's configuration to start runs";
  }
  if (!execStatus?.hasExecution) return 'Service has not run yet';
  if (execStatus.isRunning) {
    return execStatus.displayName || 'Running';
  }
  const when = execStatus.stoppedAt ? moment(execStatus.stoppedAt).fromNow() : 'recently';
  const failure = caughtOutcome(execStatus, 'FAILED');
  if (failure) {
    return failure.displayName ? `Last run failed at ${failure.displayName} — ${when}` : `Last run failed — ${when}`;
  }
  if (caughtOutcome(execStatus, 'NOT_CONFIGURED')) return `Last run skipped, not configured — ${when}`;
  const verb = TERMINAL_VERBS[execStatus.executionStatus] || 'finished';
  return `Last run ${verb} — ${when}`;
};
